import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Bell, MessageSquare, User, Home as HomeIcon, Search, Grid, Plus, Heart, MessageCircle, Share2, ChevronDown, Filter, Building, Newspaper, Calendar, Users, UserPlus, Settings, HelpCircle, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewHome() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState('Bengaluru, India');
  const [userLocation, setUserLocation] = useState(null);
  const [radius, setRadius] = useState(5000); // Default 5km
  const [likedPosts, setLikedPosts] = useState({});
  const [rangeOpen, setRangeOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const rangeOptions = [
    { label: '1 km', value: 1000 },
    { label: '2 km', value: 2000 },
    { label: '3 km', value: 3000 },
    { label: '5 km', value: 5000 },
    { label: '10 km', value: 10000 },
    { label: '15 km', value: 15000 },
    { label: '20 km', value: 20000 },
  ];

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    // Get location
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      const loc = JSON.parse(savedLocation);
      setUserLocation(loc);
      fetchPosts(loc);
    } else {
      // Default to Bengaluru
      const defaultLoc = { lat: 12.9716, lng: 77.5946 };
      setUserLocation(defaultLoc);
      localStorage.setItem('userLocation', JSON.stringify(defaultLoc));
      fetchPosts(defaultLoc);
    }
  }, []);

  const fetchPosts = async (loc) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/posts`, {
        params: {
          latitude: loc.lat,
          longitude: loc.lng,
          radius: radius,
          category: 'all'
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userLocation) {
      fetchPosts(userLocation);
    }
  }, [radius]);

  const handleLike = async (postId) => {
    if (likedPosts[postId]) {
      toast.info('Already liked!');
      return;
    }
    
    setLikedPosts(prev => ({ ...prev, [postId]: true }));
    setPosts(posts.map(p => p.id === postId ? { ...p, likes: (p.likes || 0) + 1 } : p));
    toast.success('Liked!');
  };

  const handleComment = (postId) => {
    navigate(`/post/${postId}`);
  };

  const handleShare = (postId) => {
    toast.success('Share link copied!');
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const postTime = new Date(timestamp);
    const diffInHours = Math.floor((now - postTime) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h`;
    return `${Math.floor(diffInHours / 24)}d`;
  };

  const getCategoryColor = (category) => {
    const colors = {
      free: 'bg-green-100 text-green-700',
      sale: 'bg-blue-100 text-blue-700',
      service: 'bg-purple-100 text-purple-700',
      help: 'bg-orange-100 text-orange-700',
      emergency: 'bg-red-100 text-red-700',
      network: 'bg-pink-100 text-pink-700',
      community: 'bg-yellow-100 text-yellow-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8faf9' }}>
      {/* Top Bar */}
      <header className="sticky top-0 z-50 shadow-sm" style={{ backgroundColor: '#1a4d3e' }}>
        <div className="flex items-center justify-between px-4 py-4">
          {/* Location with Range */}
          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigate('/map-view')}
              className="flex items-center gap-2 text-white hover:text-gray-200 transition"
            >
              <MapPin className="w-5 h-5" />
              <span className="font-medium text-sm">{location}</span>
              <ChevronDown className="w-4 h-4" />
            </button>
            
            {/* Range Selector Dropdown */}
            <Popover open={rangeOpen} onOpenChange={setRangeOpen}>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white text-xs font-medium transition">
                  <Filter className="w-3.5 h-3.5" />
                  {rangeOptions.find(r => r.value === radius)?.label || '5 km'}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-40 p-2" align="start">
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-500 px-2 py-1">Select Range</p>
                  {rangeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setRadius(option.value);
                        setRangeOpen(false);
                        toast.success(`Range set to ${option.label}`);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                        radius === option.value
                          ? 'bg-green-50 text-green-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/notifications')}
              className="relative"
            >
              <Bell className="w-6 h-6 text-white" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-[#1a4d3e]" />
            </button>
            <button onClick={() => navigate('/messages')}>
              <MessageSquare className="w-6 h-6 text-white" />
            </button>
            
            {/* Profile Dropdown */}
            <Popover open={profileOpen} onOpenChange={setProfileOpen}>
              <PopoverTrigger asChild>
                <button 
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-105"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                >
                  <User className="w-5 h-5 text-white" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="end">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                  {/* Profile Header */}
                  <div className="p-4" style={{ backgroundColor: '#1a4d3e' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{user?.name || 'User'}</h3>
                        <p className="text-sm text-white/80">{location}</p>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/add-business');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                      <Building className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-700 font-medium">Add business page</span>
                    </button>

                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/profile');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                      <User className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-700 font-medium">My profile</span>
                    </button>

                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/local-news');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                      <Newspaper className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-700 font-medium">Local News</span>
                    </button>

                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/events');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                      <Calendar className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-700 font-medium">Events</span>
                    </button>

                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/groups');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                      <Users className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-700 font-medium">Groups</span>
                    </button>

                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/invite');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                      <UserPlus className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-700 font-medium">Invite neighbors</span>
                    </button>

                    <div className="border-t border-gray-200 my-2"></div>

                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/settings');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                      <Settings className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-700 font-medium">Settings</span>
                    </button>

                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/help');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                      <HelpCircle className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-700 font-medium">Help Center</span>
                    </button>

                    <button
                      onClick={() => {
                        localStorage.clear();
                        toast.success('Logged out successfully');
                        navigate('/');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-red-50 transition text-red-600"
                    >
                      <LogOut className="w-5 h-5" />
                      <span className="font-medium">Log out</span>
                    </button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      {/* Main Feed */}
      <main className="pb-24 px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#1a4d3e' }}></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 px-4 bg-white rounded-2xl shadow-sm">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#1a4d3e20' }}>
              <MapPin className="w-8 h-8" style={{ color: '#1a4d3e' }} />
            </div>
            <p className="text-gray-600 mb-4">No posts in your area yet</p>
            <Button 
              onClick={() => navigate('/create-post')}
              className="rounded-full shadow-lg"
              style={{ backgroundColor: '#1a4d3e' }}
            >
              Create First Post
            </Button>
          </div>
        ) : (
          <div className="space-y-4 max-w-2xl mx-auto">
            {posts.map((post, idx) => (
              <div 
                key={post.id} 
                className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                {/* Post Header */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-11 h-11 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: '#1a4d3e20' }}
                      >
                        <User className="w-6 h-6" style={{ color: '#1a4d3e' }} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm">{post.user_name}</h4>
                        <p className="text-xs text-gray-500">{post.user_city || 'Nearby'} • {formatTime(post.created_at)}</p>
                      </div>
                    </div>
                    <span 
                      className={`px-3 py-1 text-xs rounded-full font-medium shadow-sm ${getCategoryColor(post.category)}`}
                    >
                      {post.category}
                    </span>
                  </div>

                  {/* Post Content */}
                  <div className="mb-3">
                    <h3 className="font-bold text-lg mb-2" style={{ color: '#1a4d3e' }}>{post.title}</h3>
                    <p className="text-gray-700 text-sm leading-relaxed line-clamp-3">{post.description}</p>
                  </div>
                  
                  {/* Images */}
                  {post.images && post.images.length > 0 && (
                    <div className="mb-3 flex gap-2 overflow-x-auto">
                      {post.images.slice(0, 3).map((img, i) => (
                        <img 
                          key={i}
                          src={img}
                          alt="Post"
                          className="w-28 h-28 object-cover rounded-xl shadow-sm"
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Engagement Bar */}
                <div 
                  className="flex items-center justify-around px-4 py-3.5"
                  style={{ backgroundColor: '#f8faf9' }}
                >
                  <button 
                    onClick={() => handleLike(post.id)}
                    className={`flex items-center gap-2 transition-all duration-200 ${
                      likedPosts[post.id] ? 'text-red-500' : 'text-gray-600 hover:text-red-500'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${likedPosts[post.id] ? 'fill-current' : ''}`} />
                    <span className="text-sm font-semibold">{post.likes || 0}</span>
                  </button>
                  <button 
                    onClick={() => handleComment(post.id)}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-all duration-200"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Comment</span>
                  </button>
                  <button 
                    onClick={() => handleShare(post.id)}
                    className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-all duration-200"
                  >
                    <Share2 className="w-5 h-5" />
                    <span className="text-sm font-medium">Share</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-3 max-w-md mx-auto">
          <button 
            onClick={() => navigate('/home')}
            className="flex flex-col items-center gap-1 transition-all duration-200"
          >
            <HomeIcon className="w-6 h-6" style={{ color: '#1a4d3e' }} />
            <span className="text-xs font-semibold" style={{ color: '#1a4d3e' }}>Home</span>
          </button>
          
          <button 
            onClick={() => navigate('/search')}
            className="flex flex-col items-center gap-1 transition-all duration-200 hover:scale-110"
          >
            <Search className="w-6 h-6 text-gray-600" />
            <span className="text-xs text-gray-600 font-medium">Search</span>
          </button>
          
          <button 
            onClick={() => navigate('/create-post')}
            className="flex flex-col items-center gap-1 transition-all duration-200 hover:scale-110"
          >
            <Grid className="w-6 h-6 text-gray-600" />
            <span className="text-xs text-gray-600 font-medium">For Sale</span>
          </button>
          
          {/* Post FAB */}
          <button 
            onClick={() => navigate('/create-post')}
            className="w-14 h-14 rounded-full flex flex-col items-center justify-center shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105"
            style={{ backgroundColor: '#2ecc71' }}
          >
            <Plus className="w-6 h-6 text-white mb-0.5" />
            <span className="text-[10px] text-white font-bold">Post</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
